/**
 * useAiDubbing
 *
 * Drives an AI dubbing job and exposes the same small state machine shape as
 * useAiSubtitles / useAiTrailer, so the UI renders a familiar progress panel.
 * Owns no form knowledge — it runs the service, tracks stage/progress/note, and
 * hands back the finished dubbed-track list. The Audio Tracks section decides
 * what to do with it (attach as tracks).
 *
 * Status lifecycle:
 *   idle → running → complete
 *                 ↳ failed  (retry via generate())
 *                 ↳ idle    (reset())
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { generateDub, DUB_STAGES } from "../services/ai";

export const DUB_STATUS = {
  IDLE: "idle",
  RUNNING: "running",
  COMPLETE: "complete",
  FAILED: "failed",
};

export default function useAiDubbing() {
  const [status, setStatus] = useState(DUB_STATUS.IDLE);
  const [activeStage, setActiveStage] = useState(null);
  const [progress, setProgress] = useState(0);
  const [note, setNote] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const abortRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      abortRef.current?.abort();
    };
  }, []);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setStatus(DUB_STATUS.IDLE);
    setActiveStage(null);
    setProgress(0);
    setNote(null);
    setResult(null);
    setError(null);
  }, []);

  /**
   * Start (or restart) dubbing for a video.
   * @param {object} video
   * @param {object} [opts] { targetLanguages, voice }
   */
  const generate = useCallback((video, opts = {}) => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setStatus(DUB_STATUS.RUNNING);
    setError(null);
    setResult(null);
    setProgress(0);
    setNote(null);
    setActiveStage(DUB_STAGES[0]?.id ?? null);

    generateDub({
      video,
      targetLanguages: opts.targetLanguages,
      voice: opts.voice,
      signal: controller.signal,
      onStage: (stageId, pct, stageNote) => {
        if (!mountedRef.current || controller.signal.aborted) return;
        if (stageId !== "complete") setActiveStage(stageId);
        if (typeof pct === "number") setProgress(pct);
        if (stageNote !== undefined) setNote(stageNote || null);
      },
    })
      .then((res) => {
        if (!mountedRef.current || controller.signal.aborted) return;
        setResult(res);
        setProgress(100);
        setActiveStage("complete");
        setStatus(DUB_STATUS.COMPLETE);
      })
      .catch((err) => {
        if (err?.name === "AbortError") return;
        if (!mountedRef.current) return;
        setError(err?.message || "Dub generation failed. You can retry or add audio manually.");
        setStatus(DUB_STATUS.FAILED);
      });

    return controller;
  }, []);

  return {
    status,
    stages: DUB_STAGES,
    activeStage,
    progress,
    note,
    result,
    error,
    generate,
    reset,
    isIdle: status === DUB_STATUS.IDLE,
    isRunning: status === DUB_STATUS.RUNNING,
    isComplete: status === DUB_STATUS.COMPLETE,
    isFailed: status === DUB_STATUS.FAILED,
  };
}
